/**
 * What a link row says about itself — the four-word vocabulary, and the three states a
 * recall puts a row into.
 *
 * ## Why this is a function and not JSX
 *
 * `screens.md` §5 is a **critical-fidelity** screen and `acceptance-tests.md` judges it by
 * one sentence: *"someone who has never heard of GS1 can look at the screen, seeded with the
 * Riverbend example, and correctly answer **what does a scan of batch LOT42 resolve to, and
 * why?**"* Everything below is the "why".
 *
 * A rule that lives inside a component's JSX is a rule no test in this repo can reach — c34
 * found the retire dialog's validation in exactly that position, unreachable behind an
 * `onClick`, and the test written for it was **a hand-copied reconstruction of the JSX that
 * would have stayed green while the real screen grew the affordance it existed to forbid.**
 * The rule this project keeps re-learning: *logic reachable only through an interaction will
 * be tested by a copy of itself*. So the decision is exported first and the component calls
 * it.
 *
 * ## The four words, and what each one is a claim about
 *
 * `screens.md` §5: *"Vocabulary that must survive implementation: **default / defined here /
 * inherited / replaced**. Shadowing is **per link type**, never whole-set."* They are not
 * four values of one axis — they answer two different questions, and conflating them is the
 * mistake the vocabulary exists to prevent:
 *
 *   - **defined here / inherited** — *where the row comes from* relative to the grain being
 *     viewed. A row whose own grain is the viewed one is defined here; a row from a coarser
 *     grain that still applies is inherited. This is the axis a user reads to understand
 *     why a link they did not create at LOT42 is showing under LOT42.
 *   - **replaced** — *this row is inherited **and** a finer row of the same link type
 *     shadows it for this scan.* Per link type, never whole-set: a lot-level `gs1:pip`
 *     replaces the GTIN's `gs1:pip` and leaves `gs1:allergenInfo` untouched. A UI that
 *     greyed out the whole inherited set on the appearance of any finer link would be the
 *     whole-set reading, which is wrong and is the exact misunderstanding §3.5 warns about.
 *   - **default** — *this row holds its grain's default slot*, which is what moves a plain
 *     camera scan. **Orthogonal to the other three**, which is why it is a separate field
 *     rather than a fourth value: a link can be inherited and the default, defined here and
 *     not the default, and every other combination.
 *
 * ## Why `isDefault` is not enough to say "default"
 *
 * `isDefault` is a stored designation on a row. Holding the slot *for this scan* is what
 * survives the merge's shadowing pass — a GTIN-grain link can carry the flag and still lose
 * a lot-coded plain scan to the occupant of the lot grain's slot. The merge endpoint answers
 * that question (`holdsDefaultSlot`), and where its answer is available it wins; this
 * function's own `defaultsHere` is the fallback for a grain the preview has not been run
 * for, and it is deliberately the weaker claim.
 */
import type { LinkSummary, SuppressedLink } from "@packagepage/shared";

/** The grain a user is looking at — the scan they are asking about. */
export interface ViewedGrain {
  readonly cpv: string | null;
  readonly lot: string | null;
  readonly serial: string | null;
}

/**
 * A live recall, as much of it as a row needs to explain itself.
 *
 * `suppressed` is the recall detail's `restore.suppressed` — F1's fix. Without it a
 * suppressed row is indistinguishable from a row the user cleared themselves, which is the
 * state §1.3 exists to stop the screen showing.
 */
export interface RecallContext {
  readonly id: string;
  readonly scope: "gtin" | "lots";
  readonly lots: readonly string[];
  readonly suppressed: readonly SuppressedLink[];
}

/**
 * The three states a recall puts a row into, plus the ordinary one.
 *
 * A discriminant rather than a set of booleans, because the screen switches on it and
 * c31b's lesson from the consuming side is to **switch on the discriminant, never on the
 * class** — and because three booleans admit five combinations that mean nothing.
 */
export type RecallRowState =
  | { readonly kind: "none" }
  /**
   * The recall flow created this row. Controls disabled — **and that is UI courtesy, not
   * enforcement.** `RECALL_LINK_LOCKED` is a real 409 the API raises whatever the screen
   * disabled, and the row must render that refusal's own `detail` rather than composing a
   * message of its own: the API's copy names the recall id and the lift path, and a second
   * wording would be a second source for a sentence a person acts on during an incident.
   */
  | { readonly kind: "materialised"; readonly recallId: string }
  /**
   * This row's `isDefault` was cleared by the recall's suppression sweep and comes back on
   * lift. The row is otherwise **ordinary and editable**.
   *
   * `deletionIsPermanent` is the consequence §3.8's tolerance rule makes real and which
   * `screens.md` requires be stated *before* a user can cause it: a suppressed row deleted
   * mid-recall is **skipped at lift and does not come back**. Lift records the skip rather
   * than failing, so nothing will tell the user afterwards either.
   */
  | {
      readonly kind: "suppressed";
      readonly recallId: string;
      readonly scopeDescription: string;
      readonly deletionIsPermanent: true;
    };

export interface LinkRowLabels {
  readonly link: LinkSummary;
  /** `defined here` or `inherited` — where this row's grain sits relative to the viewed one. */
  readonly origin: "defined here" | "inherited";
  /** Inherited **and** shadowed by a finer row of the same link type, for this scan. */
  readonly replaced: boolean;
  /** It holds this grain's default slot — what moves a plain camera scan. */
  readonly holdsDefaultSlot: boolean;
  readonly recall: RecallRowState;
  /**
   * Where plain scans of the viewed grain go **while this row is suspended**, or null.
   *
   * The collateral, stated where it bites (`screens.md` §5, addendum §1.4). A cpv- or
   * serial-grain default suppressed by a lot-level recall is deliberate, temporary product
   * degradation for units nobody recalled — and without this sentence it reads as a bug.
   */
  readonly meanwhileDestination: string | null;
}

const grainOf = (link: LinkSummary): ViewedGrain => ({
  cpv: link.grain.cpv ?? null,
  lot: link.grain.lot ?? null,
  serial: link.grain.serial ?? null,
});

const sameGrain = (a: ViewedGrain, b: ViewedGrain): boolean =>
  a.cpv === b.cpv && a.lot === b.lot && a.serial === b.serial;

/** How many qualifiers a grain names. The merge's own specificity measure (§3.5). */
const specificity = (grain: ViewedGrain): number =>
  (grain.cpv === null ? 0 : 1) +
  (grain.lot === null ? 0 : 1) +
  (grain.serial === null ? 0 : 1);

/**
 * Does this link apply to a scan of the viewed grain?
 *
 * A link applies when every qualifier it names matches the scan's. A link naming a
 * qualifier the scan does not carry does **not** apply — which is what makes a LOT42 link
 * invisible to a plain GTIN scan, and is the half of inheritance users find surprising.
 */
const appliesTo = (link: LinkSummary, viewed: ViewedGrain): boolean => {
  const grain = grainOf(link);
  if (grain.cpv !== null && grain.cpv !== viewed.cpv) return false;
  if (grain.lot !== null && grain.lot !== viewed.lot) return false;
  if (grain.serial !== null && grain.serial !== viewed.serial) return false;
  return true;
};

/** *"lots LOT42, LOT77"* / *"the whole GTIN"* — the phrase the suppressed row's copy uses. */
export function describeRecallScope(recall: RecallContext): string {
  return recall.scope === "gtin"
    ? "the whole GTIN"
    : `lot${recall.lots.length === 1 ? "" : "s"} ${recall.lots.join(", ")}`;
}

export interface LabelInput {
  readonly links: readonly LinkSummary[];
  readonly viewed: ViewedGrain;
  /** Active recalls on this GTIN. Empty is the ordinary case and must stay legible. */
  readonly recalls: readonly RecallContext[];
  /**
   * `holdsDefaultSlot` per link id, from the merge preview, when it has been run for this
   * grain.
   *
   * **Optional, and the fallback is deliberately the weaker claim.** The endpoint is the
   * authority on which link holds a slot for a given scan — it runs the real merge. When it
   * has not been consulted, this function computes the same thing from the rows and can be
   * wrong in the one direction that matters least: it does not know about validity windows,
   * because those depend on a clock this function does not read. A row inside an expired
   * window would be shown as the default when a scan would fall past it. Rendering the
   * preview's answer where there is one is what makes the screen and the simulator agree.
   */
  readonly holdsDefaultSlot?: ReadonlySet<string> | undefined;
  /** Where a plain scan of the viewed grain lands right now, from the preview. */
  readonly currentDestination?: string | null | undefined;
}

/**
 * Label every link that applies to the viewed grain.
 *
 * Rows that do not apply are dropped: this is a view of *one scan*, and a LOT77 link under a
 * LOT42 view answers a different question. The link list for the whole product is a
 * different screen state, reached by viewing the GTIN grain.
 */
export function labelLinkRows(input: LabelInput): LinkRowLabels[] {
  const applying = input.links.filter((link) => appliesTo(link, input.viewed));

  // Per link **type** — the whole-set reading is the misunderstanding the vocabulary
  // exists to prevent. A finer `gs1:pip` replaces the GTIN's `gs1:pip` and leaves every
  // other relation exactly where it was.
  const deepestByType = new Map<string, number>();
  for (const link of applying) {
    const depth = specificity(grainOf(link));
    const seen = deepestByType.get(link.linkType);
    if (seen === undefined || depth > seen) deepestByType.set(link.linkType, depth);
  }

  const suppressedBy = new Map<string, RecallContext>();
  for (const recall of input.recalls) {
    for (const entry of recall.suppressed) suppressedBy.set(entry.linkId, recall);
  }

  return applying.map((link): LinkRowLabels => {
    const grain = grainOf(link);
    const origin = sameGrain(grain, input.viewed) ? "defined here" : "inherited";
    const replaced =
      origin === "inherited" &&
      specificity(grain) < (deepestByType.get(link.linkType) ?? 0);

    const suppressing = suppressedBy.get(link.id);
    const recall: RecallRowState =
      link.recallId !== null
        ? { kind: "materialised", recallId: link.recallId }
        : suppressing !== undefined
          ? {
              kind: "suppressed",
              recallId: suppressing.id,
              scopeDescription: describeRecallScope(suppressing),
              deletionIsPermanent: true,
            }
          : { kind: "none" };

    return {
      link,
      origin,
      replaced,
      holdsDefaultSlot:
        input.holdsDefaultSlot?.has(link.id) ??
        (link.isDefault &&
          !replaced &&
          specificity(grain) === (deepestByType.get(link.linkType) ?? 0)),
      recall,
      // Only for a suppressed row, and only when the preview has told us where scans of
      // this grain actually go. Composing a guess here would be the client-side merge the
      // whole endpoint exists to avoid, one sentence smaller.
      meanwhileDestination:
        recall.kind === "suppressed" ? (input.currentDestination ?? null) : null,
    };
  });
}
